import type { Tag } from '@captain-tasko/shared';

/**
 * A tag chip. By default it renders as a static <span>, matching the
 * old look. Pass `onClick` to make it interactive — used by the
 * Documents list (filter by that tag) and the Tags admin page (open
 * the documents filtered by that tag).
 *
 * When clickable, we render as a <button type="button"> so it picks
 * up keyboard focus + Enter/Space activation for free, and we
 * stopPropagation so a click inside a row doesn't also trigger the
 * row's onClick (the Documents list table rows navigate to the doc
 * detail on click).
 */
export function TagBadge({
  tag,
  onClick,
  title,
}: {
  tag: Tag;
  onClick?: (tag: Tag) => void;
  /** Override the default hover title (description ?? name). */
  title?: string;
}) {
  const tip = title ?? tag.description ?? `Filter by ${tag.name}`;

  if (onClick) {
    return (
      <button
        type="button"
        className="tag-chip tag-chip--clickable"
        title={tip}
        onClick={(e) => {
          e.stopPropagation();
          onClick(tag);
        }}
      >
        <span className="tag-chip__dot" style={{ background: tag.color }} aria-hidden="true" />
        {tag.name}
      </button>
    );
  }

  return (
    <span className="tag-chip" title={tag.description ?? tag.name}>
      <span className="tag-chip__dot" style={{ background: tag.color }} aria-hidden="true" />
      {tag.name}
    </span>
  );
}
